import type { GameState, ShotResult, Team } from '../shared/types';
import classicUi from '../shared/classic-ui.json';
import { sourceFrameBlend } from './source-frame-blend';

/** The server owns readyAt/deadline; this timeline only presents their hand-off. */
export const TURN_TRANSITION_MS = 680;
export const TURN_CONTROL_SETTLE_MS = 240;

interface PresentedTurn {
  key: string;
  playerId: string;
  name: string;
  team: Team;
  number: number;
  wind: number;
}

export interface TurnTransitionFrame extends PresentedTurn {
  /** Continuous decoration time; authoritative hand-off and input use readyAt. */
  elapsed: number;
  stage: 'idle' | 'settling' | 'entering' | 'holding' | 'leaving';
  visible: boolean;
  opacity: number;
  translateX: number;
  scale: number;
  isSelf: boolean;
  inputLocked: boolean;
  previousWind: number | null;
}

const clamp = (value: number) => Math.max(0, Math.min(1, value));
const easeOut = (value: number) => 1 - (1 - clamp(value)) ** 3;
const smoothstep = (value: number) => { const t = clamp(value); return t * t * (3 - 2 * t); };

export function turnKey(state: GameState): string {
  return `${state.round}:${state.turn.number}:${state.turn.playerId}`;
}

/** Preserve the vane's native 560ms loop while resampling between its frames. */
export function windArrowSample(elapsedMs: number) {
  const asset = classicUi.assets.windArrow;
  const sample = sourceFrameBlend(elapsedMs, asset.frameRate, asset.frames.length, true);
  return { ...sample, path: asset.frames[sample.frameIndex], nextPath: asset.frames[sample.nextFrameIndex] };
}

/** The existing banner keeps its geometry and duration; only its glow animates. */
export function turnGlowSample(elapsedMs: number) {
  const asset = classicUi.assets.turnGlow;
  const sample = sourceFrameBlend(elapsedMs, asset.frameRate, asset.frames.length, true);
  return { ...sample, path: asset.frames[sample.frameIndex], nextPath: asset.frames[sample.nextFrameIndex] };
}

/**
 * The recovered client's ChangePlayerAction waits for moving objects, then
 * focuses the next living and plays TurnAsset. Do the same with authoritative
 * readyAt rather than advancing the browser's physics or delaying the server.
 */
export class TurnTransition {
  private current: PresentedTurn | null = null;
  private previous: PresentedTurn | null = null;
  private startsAt = 0;
  private fromWind = 0;
  private animate = false;
  private finished = false;

  constructor(private readonly reducedMotion = false) {}

  observe(state: GameState, shot?: ShotResult, serverNow = Date.now()): void {
    if (state.phase === 'lobby') { this.reset(); return; }
    const player = state.players.find((entry) => entry.id === state.turn.playerId);
    const next: PresentedTurn = {
      key: turnKey(state), playerId: state.turn.playerId, name: player?.name ?? '弹友',
      team: player?.team ?? 0, number: state.turn.number, wind: state.turn.wind,
    };
    if (state.phase === 'finished') {
      // GameOverAction leaves the current living in place while the final
      // projectile/fall settles. A departure can end a game after nextTurn
      // was scheduled but before that new living was ever presented.
      const presented = this.sample(serverNow);
      this.current = presented ? {
        key: presented.key, playerId: presented.playerId, name: presented.name,
        team: presented.team, number: presented.number, wind: presented.wind,
      } : next;
      this.previous = null;
      this.animate = false;
      this.finished = true;
      return;
    }
    this.finished = false;
    if (this.current?.key === next.key) { this.current = next; return; }
    const sampled = this.sample(serverNow);
    this.previous = this.current ? { ...this.current, wind: sampled?.wind ?? this.current.wind } : null;
    if (!this.previous && shot && serverNow < state.turn.readyAt) {
      const firingPlayer = state.players.find((entry) => entry.id === shot.playerId);
      if (firingPlayer) this.previous = { ...next, playerId: firingPlayer.id, name: firingPlayer.name, team: firingPlayer.team, number: Math.max(1, next.number - 1) };
    }
    this.fromWind = this.previous?.wind ?? next.wind;
    this.current = next;
    // A reconnect or tab resumed mid-turn must not replay an old introduction.
    // A fresh shot can arrive late; follow its original readyAt in that case.
    this.startsAt = state.turn.readyAt;
    const duration = this.duration;
    this.animate = serverNow < this.startsAt + duration && state.turn.deadline > serverNow;
    if (!shot && this.startsAt < serverNow - duration) this.animate = false;
  }

  sample(serverNow = Date.now(), selfId = ''): TurnTransitionFrame | null {
    const current = this.current;
    if (!current) return null;
    const elapsed = serverNow - this.startsAt;
    const base = {
      ...current, isSelf: current.playerId === selfId,
      elapsed: Math.max(0, elapsed),
      previousWind: this.previous?.wind ?? null,
      visible: false, opacity: 0, translateX: 0, scale: 1,
      inputLocked: this.finished,
    };
    if (this.finished) return { ...base, stage: 'idle' };
    if (elapsed < 0) {
      const presented = this.previous ?? current;
      return { ...base, ...presented, isSelf: presented.playerId === selfId, stage: 'settling', inputLocked: true };
    }
    if (!this.animate || elapsed >= this.duration) return { ...base, stage: 'idle' };
    const entrance = this.reducedMotion ? 60 : 170;
    const exit = this.reducedMotion ? 60 : 170;
    const leavingAt = this.duration - exit;
    const stage = elapsed < entrance ? 'entering' : elapsed < leavingAt ? 'holding' : 'leaving';
    const inProgress = easeOut(elapsed / entrance);
    const outProgress = smoothstep((elapsed - leavingAt) / exit);
    return {
      ...base, stage, visible: true,
      wind: this.fromWind + (current.wind - this.fromWind) * smoothstep(elapsed / (this.reducedMotion ? 100 : 420)),
      opacity: stage === 'leaving' ? 1 - outProgress : inProgress,
      translateX: this.reducedMotion ? 0 : stage === 'leaving' ? 42 * outProgress : -58 * (1 - inProgress),
      scale: this.reducedMotion ? 1 : .94 + .06 * inProgress,
      inputLocked: elapsed < (this.reducedMotion ? 80 : TURN_CONTROL_SETTLE_MS),
    };
  }

  private get duration() { return this.reducedMotion ? 180 : TURN_TRANSITION_MS; }

  reset(): void {
    this.current = null; this.previous = null; this.startsAt = 0;
    this.animate = false; this.fromWind = 0;
    this.finished = false;
  }
}
